import { Spinner } from "@dropbox/dig-components/progress_indicators";
import { Text } from "@dropbox/dig-components/typography";
import { InfiniteScroll } from "@mirage/feed/components/InfiniteScroll/InfiniteScroll";
import { NoResults } from "@mirage/search/SearchResults/NoResults";
import { useMultimediaSearch } from "@mirage/service-search/hooks/useMultimediaSearch";
import { useResultPAPLogger } from "@mirage/shared/hooks/useResultPAPLogger";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelectedMultimediaSearchResult } from "./hooks/useSelectedMultimediaSearchResult";
import { MultimediaResultItem } from "./MultimediaResultItem";
import styles from "./MultimediaSearchResults.module.css";

import { Button } from "@dropbox/dig-components/buttons";
import { UIIcon } from "@dropbox/dig-icons";
import { InfoLine } from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashMultimedia } from "@mirage/analytics/events/types/click_dash_multimedia";
import type { SearchResult } from "@mirage/service-dbx-api";
import type { ScoredResult } from "@mirage/service-search/service";
import {
  concatActiveFilterBindings,
  type SearchFilter,
} from "@mirage/shared/search/search-filters";

type MultimediaSearchResultsProps = {
  query: string;
  filters: SearchFilter[];
  resetAllFilters: () => void;
  results?: ScoredResult[];
  loading: boolean;
};
export const MultimediaSearchResults: React.FC<
  MultimediaSearchResultsProps
> = ({ query, filters, resetAllFilters, results, loading }) => {
  const {
    loadMore,
    data: fssResults,
    loading: fssLoading,
  } = useMultimediaSearch(query, filters);
  const { logSearchResultOpen } = useResultPAPLogger();
  const { reportPapEvent, searchSessionManager } = useMirageAnalyticsContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const mediaResults = useMemo(() => {
    if (results) {
      return results;
    } else {
      return fssResults;
    }
  }, [results, fssResults]);

  const [imageLoadedCount, setImageLoadedCount] = useState(0);

  const imageLoaded = () => {
    setImageLoadedCount((prev) => prev + 1);
  };

  useEffect(() => {
    if (loading) {
      setImageLoadedCount(0);
    }
  }, [loading]);

  const { openMultimediaSearchResult } = useSelectedMultimediaSearchResult();

  const MAX_THRESHOLD_DEFAULT = 8;

  const maxThreshold = useMemo(() => {
    if (loading || mediaResults.length > MAX_THRESHOLD_DEFAULT) {
      return MAX_THRESHOLD_DEFAULT;
    }
    return mediaResults.length - 1;
  }, [mediaResults.length, loading]);

  const handleResultClick = useCallback(
    (result: SearchResult, index: number) => {
      openMultimediaSearchResult(result, {
        count: mediaResults.length,
        index: index,
        next:
          index < mediaResults.length - 1
            ? () => handleResultClick(mediaResults[index + 1], index + 1)
            : undefined,
        prev:
          index > 0
            ? () => handleResultClick(mediaResults[index - 1], index - 1)
            : undefined,
      });
      logSearchResultOpen({
        defaultProps: {
          searchResult: result,
          query,
          filters,
          results: fssResults,
          featureLine: "search",
        },
        rightRailOpened: false,
        isTypeahead: false,
        launchMethod: "click",
        actionSurfaceComponent: "result_row",
      });
      reportPapEvent(
        PAP_Click_DashMultimedia({
          searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
          fileTypeCategory: result.fileTypeInfo?.id,
          activeFilters: concatActiveFilterBindings(filters),
        }),
      );
    },
    [filters, fssResults, logSearchResultOpen, query, mediaResults],
  );

  const imagesLoading = useMemo(() => {
    return results ? (results.length > 0 ? false : loading) : fssLoading;
  }, [loading, fssLoading, results]);

  const title = useMemo(() => {
    if (filters.find((filter) => filter.id === "image")) {
      return i18n.t("images");
    }
    if (filters.find((filter) => filter.id === "video")) {
      return i18n.t("videos");
    }
    return i18n.t("media");
  }, [filters]);

  const infoButtonLabel = useMemo(() => {
    if (filters.find((filter) => filter.id === "image")) {
      return i18n.t("about_image_search");
    }
    if (filters.find((filter) => filter.id === "video")) {
      return i18n.t("about_video_search");
    }
    return i18n.t("about_media_search");
  }, [filters]);

  if (!imagesLoading && isEmpty(mediaResults)) {
    return (
      <NoResults activeFilters={filters} resetAllFilters={resetAllFilters} />
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.multimediaHeader}>
        <Text variant="label" color="standard" size="large">
          {title}
        </Text>
        {!loading && (
          <Text variant="label" color="subtle" size="small">
            {mediaResults.length}
          </Text>
        )}
        <Button
          href="https://help.dropbox.com/view-edit/dropbox-dash-search-and-explore"
          withIconStart={<UIIcon src={InfoLine} />}
          target="_blank"
          className={styles.multimediaAbout}
          variant="borderless"
          size="small"
        >
          {infoButtonLabel}
        </Button>
      </div>
      <div
        ref={containerRef}
        className={classNames(styles.container, styles.expanded)}
      >
        {(imagesLoading || imageLoadedCount <= maxThreshold) &&
          new Array(12).fill(0).map((_, i) => (
            <div role="button" className={styles.item} key={i}>
              <MultimediaResultItem />
            </div>
          ))}
        {mediaResults
          .slice(
            0,
            imageLoadedCount < maxThreshold
              ? maxThreshold
              : mediaResults.length,
          )
          .map((res, i) => (
            <MultimediaResultItem
              key={i}
              result={res}
              query={query}
              className={classNames(
                styles.imagesContainer,
                imageLoadedCount > maxThreshold ? styles.fadeIn : "",
              )}
              onLoad={() => imageLoaded()}
              onError={() => imageLoaded()}
              onClick={(res) => handleResultClick(res, i)}
            />
          ))}
        {!imagesLoading && !results && <InfiniteScroll onTrigger={loadMore} />}
      </div>
      {imagesLoading && mediaResults.length > 0 && (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      )}
    </div>
  );
};
